import { useEffect, useMemo, useState } from "react";
import { Icon } from "../Icon";
import { Button, Card, Pill } from "../ui";
import type { ReflectionDefinition } from "../../puzzles/puzzlePackage";
import {
  createReflectionSession,
  scoreReflectionAnswers,
  type ReflectionAnswerValue,
} from "../../puzzles/reflectionRuntime";

export function ReflectionPlayer({
  definition,
  fallbackPrompt,
  seed,
  busy,
  onBack,
  onSubmit,
}: {
  definition?: ReflectionDefinition;
  fallbackPrompt: string;
  seed: number;
  busy: boolean;
  onBack(): void;
  onSubmit(payload: string): void;
}) {
  const resolvedDefinition =
    definition ?? ({ mode: "text", prompt: fallbackPrompt, minLength: 10 } as const);

  if (resolvedDefinition.mode === "quiz") {
    return (
      <QuizReflection
        definition={resolvedDefinition}
        seed={seed}
        busy={busy}
        onBack={onBack}
        onSubmit={onSubmit}
      />
    );
  }

  return (
    <TextReflection
      definition={resolvedDefinition}
      busy={busy}
      onBack={onBack}
      onSubmit={onSubmit}
    />
  );
}

function TextReflection({
  definition,
  busy,
  onBack,
  onSubmit,
}: {
  definition: Extract<ReflectionDefinition, { mode: "text" }>;
  busy: boolean;
  onBack(): void;
  onSubmit(payload: string): void;
}) {
  const [reflection, setReflection] = useState("");
  const [remainingSeconds, timedOut] = useCountdown(definition.timeLimitSeconds);
  const trimmedLength = reflection.trim().length;
  const tooLong =
    typeof definition.maxLength === "number" &&
    reflection.length > definition.maxLength;
  const canSubmit =
    !busy &&
    !tooLong &&
    (timedOut || trimmedLength >= definition.minLength);

  return (
    <Card className="mx-auto max-w-3xl p-7 sm:p-9">
      <ReflectionHeader
        eyebrow="Reflection"
        title="Explain what you noticed"
        detail={definition.prompt}
        timer={remainingSeconds}
      />
      <label htmlFor="reflection" className="sr-only">
        Quest reflection
      </label>
      <textarea
        id="reflection"
        rows={6}
        value={reflection}
        onChange={(event) => setReflection(event.target.value)}
        disabled={busy || timedOut}
        maxLength={definition.maxLength}
        placeholder="Write a few sentences about the relationship you observed..."
        className="mt-7 w-full rounded-2xl border border-slate-700 bg-slate-950 p-4 text-sm leading-6 text-white placeholder:text-slate-600 disabled:opacity-60"
      />
      <div className="mt-3 flex items-center justify-between gap-3 text-xs text-slate-500">
        <span>
          Minimum {definition.minLength} characters
          {definition.maxLength ? `, maximum ${definition.maxLength}` : ""}
        </span>
        <span className={tooLong ? "font-bold text-rose-300" : ""}>
          {trimmedLength}
        </span>
      </div>
      <ReflectionActions
        busy={busy}
        canSubmit={canSubmit}
        onBack={onBack}
        onSubmit={() => onSubmit(reflection.trim())}
      />
    </Card>
  );
}

function QuizReflection({
  definition,
  seed,
  busy,
  onBack,
  onSubmit,
}: {
  definition: Extract<ReflectionDefinition, { mode: "quiz" }>;
  seed: number;
  busy: boolean;
  onBack(): void;
  onSubmit(payload: string): void;
}) {
  const session = useMemo(
    () => createReflectionSession(definition, seed),
    [definition, seed],
  );
  const [answers, setAnswers] = useState<Record<string, ReflectionAnswerValue>>({});
  const [remainingSeconds, timedOut] = useCountdown(definition.timeLimitSeconds);
  const allAnswered = session.questions.every((item) => {
    const answer = answers[item.question.id];
    return Array.isArray(answer) ? answer.length > 0 : Boolean(answer);
  });
  const canSubmit = !busy && (allAnswered || timedOut);

  const submit = () => {
    const score = scoreReflectionAnswers(session, answers);
    onSubmit(
      JSON.stringify({
        mode: "quiz",
        title: definition.title,
        submittedAt: new Date().toISOString(),
        timedOut,
        score,
        answers,
        questions: session.questions.map((item) => ({
          id: item.question.id,
          prompt: item.prompt,
          type: item.question.type,
          variables: item.variables,
        })),
      }),
    );
  };

  return (
    <Card className="mx-auto max-w-4xl p-7 sm:p-9">
      <ReflectionHeader
        eyebrow="Reflection quiz"
        title={definition.title}
        detail={definition.instructions ?? "Answer the questions below."}
        timer={remainingSeconds}
      />

      <div className="mt-7 space-y-5">
        {session.questions.map((item, index) => (
          <div
            key={item.question.id}
            className="rounded-2xl border border-slate-800 bg-slate-950/55 p-5"
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <p className="font-retro text-[8px] uppercase tracking-[0.18em] text-slate-500">
                  Question {index + 1}
                </p>
                <h3 className="mt-2 text-base font-bold leading-6 text-white">
                  {item.prompt}
                </h3>
              </div>
              <Pill tone="slate">{item.question.points} pts</Pill>
            </div>
            {item.question.type === "multiple-choice" ? (
              <MultipleChoiceQuestion
                item={item}
                value={answers[item.question.id]}
                disabled={busy || timedOut}
                onChange={(value) =>
                  setAnswers((current) => ({
                    ...current,
                    [item.question.id]: value,
                  }))
                }
              />
            ) : (
              <CalculationQuestion
                item={item}
                value={answers[item.question.id]}
                disabled={busy || timedOut}
                onChange={(value) =>
                  setAnswers((current) => ({
                    ...current,
                    [item.question.id]: value,
                  }))
                }
              />
            )}
          </div>
        ))}
      </div>

      <ReflectionActions
        busy={busy}
        canSubmit={canSubmit}
        onBack={onBack}
        onSubmit={submit}
        submitLabel={timedOut ? "Submit timed response" : "Submit quiz"}
      />
    </Card>
  );
}

function MultipleChoiceQuestion({
  item,
  value,
  disabled,
  onChange,
}: {
  item: ReturnType<typeof createReflectionSession>["questions"][number];
  value?: ReflectionAnswerValue;
  disabled: boolean;
  onChange(value: ReflectionAnswerValue): void;
}) {
  if (item.question.type !== "multiple-choice") return null;
  const question = item.question;
  const selected = Array.isArray(value) ? value : value ? [value] : [];

  return (
    <div className="mt-4 grid gap-3">
      {(item.choices ?? question.choices).map((choice) => {
        const checked = selected.includes(choice.id);
        return (
          <label
            key={choice.id}
            className="flex cursor-pointer items-start gap-3 rounded-xl border border-slate-800 bg-slate-900/55 p-3 text-sm text-slate-200 has-[:checked]:border-cyan-400/40 has-[:checked]:bg-cyan-400/10"
          >
            <input
              type={question.allowMultiple ? "checkbox" : "radio"}
              name={question.id}
              checked={checked}
              disabled={disabled}
              onChange={(event) => {
                if (question.allowMultiple) {
                  onChange(
                    event.target.checked
                      ? [...selected, choice.id]
                      : selected.filter((choiceId) => choiceId !== choice.id),
                  );
                } else {
                  onChange(choice.id);
                }
              }}
              className="mt-1"
            />
            <span>{choice.text}</span>
          </label>
        );
      })}
    </div>
  );
}

function CalculationQuestion({
  item,
  value,
  disabled,
  onChange,
}: {
  item: ReturnType<typeof createReflectionSession>["questions"][number];
  value?: ReflectionAnswerValue;
  disabled: boolean;
  onChange(value: ReflectionAnswerValue): void;
}) {
  if (item.question.type !== "calculation") return null;

  return (
    <div className="mt-4">
      {item.variables && Object.keys(item.variables).length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {Object.entries(item.variables).map(([key, variableValue]) => (
            <Pill key={key} tone="cyan">
              {key} = {variableValue}
            </Pill>
          ))}
        </div>
      )}
      <label className="block">
        <span className="text-xs font-bold text-slate-300">
          Calculated value{item.question.unit ? ` (${item.question.unit})` : ""}
        </span>
        <input
          type="number"
          inputMode="decimal"
          value={typeof value === "string" ? value : ""}
          disabled={disabled}
          onChange={(event) => onChange(event.target.value)}
          className="mt-2 w-full rounded-xl border border-slate-700 bg-slate-950 p-3 text-sm text-white disabled:opacity-60"
        />
      </label>
      <p className="mt-2 text-xs text-slate-500">
        Accepted error: +/- {item.question.tolerance}
        {item.question.unit ? ` ${item.question.unit}` : ""}
      </p>
    </div>
  );
}

function ReflectionHeader({
  eyebrow,
  title,
  detail,
  timer,
}: {
  eyebrow: string;
  title: string;
  detail: string;
  timer?: number;
}) {
  return (
    <div className="flex items-start justify-between gap-4">
      <div className="flex items-start gap-4">
        <span className="rounded-2xl bg-violet-400/10 p-3 text-violet-300">
          <Icon name="BrainCircuit" size={25} />
        </span>
        <div>
          <p className="font-retro text-[8px] uppercase tracking-widest text-violet-300">
            {eyebrow}
          </p>
          <h2 className="mt-2 font-display text-3xl font-bold text-white">
            {title}
          </h2>
          <p className="mt-3 leading-7 text-slate-400">{detail}</p>
        </div>
      </div>
      {typeof timer === "number" && (
        <Pill tone={timer <= 10 ? "rose" : "amber"}>{formatTimer(timer)}</Pill>
      )}
    </div>
  );
}

function ReflectionActions({
  busy,
  canSubmit,
  onBack,
  onSubmit,
  submitLabel = "Submit expedition",
}: {
  busy: boolean;
  canSubmit: boolean;
  onBack(): void;
  onSubmit(): void;
  submitLabel?: string;
}) {
  return (
    <div className="mt-5 flex flex-col gap-3 sm:flex-row sm:justify-end">
      <Button variant="secondary" onClick={onBack} disabled={busy}>
        Adjust answer
      </Button>
      <Button onClick={onSubmit} disabled={!canSubmit}>
        <Icon name="Rocket" size={18} />
        {busy ? "Validating..." : submitLabel}
      </Button>
    </div>
  );
}

function useCountdown(initialSeconds?: number): [number | undefined, boolean] {
  const [remaining, setRemaining] = useState(initialSeconds);

  useEffect(() => {
    setRemaining(initialSeconds);
  }, [initialSeconds]);

  useEffect(() => {
    if (typeof remaining !== "number" || remaining <= 0) return undefined;
    const timer = window.setInterval(() => {
      setRemaining((current) =>
        typeof current === "number" ? Math.max(0, current - 1) : current,
      );
    }, 1000);
    return () => window.clearInterval(timer);
  }, [remaining]);

  return [remaining, typeof remaining === "number" && remaining <= 0];
}

function formatTimer(seconds: number) {
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return `${minutes}:${String(remainder).padStart(2, "0")}`;
}
